import { useDispatch, useSelector } from 'react-redux'
import submissionSelectors from '../../../selectors/submission.selectors'
import { submissionActions } from '../../../slices/submission.slice'
import { useFetchQuestionsQuery } from '../../question.api'
import textSubmissionDrawerSelectors from '../../../selectors/text-submission-drawer.selectors'
import { useEffect, useState, useRef } from 'react'
import { textSubmissionDrawerActions, ViewModes } from '../../../slices/text-submission-drawer.slice'
import useSubmissionGroups from '../../../../pages/assignment/sections/submissions/submission-groups/useSubmissionGroups'
import baseApi from '../../base.api'
import { useParams, useNavigate, useLocation } from 'react-router'
import { useVersionId } from '../../../../utils/parseURL.helper'
import { useFetchTextSubmissionDetailsQuery } from '../../submission.api'
import { isNil, isNumber } from 'lodash'
import { getQuestionById } from '../selectors/question.api.selectors'
import { GradingModes } from '../../../../pages/assignment/components/text-submission-drawer/header/GradingModeSelect'
import userSelectors from '../../../selectors/user.selectors'
import {
    FLAGGED_PARAM,
    FOCUS_QUESTION_ID_PARAM,
    OPEN_SUBMISSION_ID_PARAM,
    RESUME_FROM_BUTTON_PARAM,
} from '../../../../constants/query-params'
import useQueryParamSetter from '../../../../hooks/useQueryParamSetter'
import useQueryParams from '../../../../hooks/useQueryParams'
import useQueryParam from '../../../../hooks/useQueryParam'
import { ExplainabilityStatuses } from '../../../../constants/explainability-statuses'
import assignmentSelectors from '../../../selectors/assignment.selectors'
import { flatten } from '../../../../utils/array.helper'
import Routes from '../../../../constants/routes'
import { AssignmentSections } from '../../../../constants/assignment-sections'
import { actions as assignmentActions } from '../../../reducers/assignment.reducer'
import { unstartRefinementProcess } from '../../../slices/rubric-refinement.slice'

const prefetchSubmissions = (dispatch, submissionIds) =>
    submissionIds.map(id => dispatch(baseApi.endpoints.fetchTextSubmissionDetails.initiate({ submissionId: id })))

// Note: group-scoped helpers removed in favor of global navigation helpers

// Global navigation helpers across all groups
const getAllSubmissionIdsInOrder = groups => groups?.flatMap(group => group?.submissions?.map(s => s.id) || []) || []

const getGlobalNextSubmissionIds = (groups, currentSubmissionId, take = 3) => {
    const allIds = getAllSubmissionIdsInOrder(groups)
    const idx = allIds.indexOf(currentSubmissionId)
    if (idx === -1) return []
    return allIds.slice(idx + 1, idx + 1 + take)
}

const getGlobalPreviousSubmissionIds = (groups, currentSubmissionId, take = 3) => {
    const allIds = getAllSubmissionIdsInOrder(groups)
    const idx = allIds.indexOf(currentSubmissionId)
    if (idx === -1) return []
    const start = Math.max(0, idx - take)
    return allIds.slice(start, idx).reverse()
}

const getUnapprovedQuestions = (questions, answers, questionIdToSkip, userId, isOwner) => {
    if (!questions || !answers) return []

    return questions.filter(question => {
        if (question.id === questionIdToSkip) return false
        const answer = answers.find(answer => answer.questionId === question.id)
        return !answer?.approveStatus && (isNil(answer?.assigneeId) || answer.assigneeId === userId || isOwner)
    })
}

export const useTextSubmissionNavigation = () => {
    const dispatch = useDispatch()
    const openedSubmissionId = useSelector(submissionSelectors.openedSubmissionId)
    const groupsAtSubmissionOpenMoment = useSelector(submissionSelectors.groupsAtSubmissionOpenMoment)
    const gradingMode = useSelector(textSubmissionDrawerSelectors.gradingMode)
    const userId = useSelector(userSelectors.id)
    const isOwner = useSelector(assignmentSelectors.isOwner)

    const { token, layerId: questionRubricIdParam } = useParams()
    const questionRubricId = Number(questionRubricIdParam)
    const versionId = useVersionId()

    const { groups: currentGroups } = useSubmissionGroups()
    const groups = groupsAtSubmissionOpenMoment || currentGroups

    const { data: questions } = useFetchQuestionsQuery({ token, versionId })

    const { data: submission } = useFetchTextSubmissionDetailsQuery(
        { submissionId: openedSubmissionId },
        { skip: !openedSubmissionId },
    )

    const allQuestionRubrics = questions?.flatMap(q => q.questionRubrics || []) || []

    const navigateToPreviousSubmission = () => {
        const previousIds = getGlobalPreviousSubmissionIds(groups, openedSubmissionId, 3)
        if (previousIds.length === 0) return
        dispatch(submissionActions.openSubmission({ id: previousIds[0], token, versionId, questionRubricId }))
        if (isNumber(questionRubricId))
            dispatch(textSubmissionDrawerActions.setFocusedQuestionRubricId(questionRubricId))
        prefetchSubmissions(dispatch, previousIds)
    }

    const navigateToNextSubmission = () => {
        const nextIds = getGlobalNextSubmissionIds(groups, openedSubmissionId, 3)
        if (nextIds.length === 0) return
        dispatch(submissionActions.openSubmission({ id: nextIds[0], token, versionId, questionRubricId }))
        if (isNumber(questionRubricId))
            dispatch(textSubmissionDrawerActions.setFocusedQuestionRubricId(questionRubricId))
        prefetchSubmissions(dispatch, nextIds)
    }

    // Get unapproved questionRubrics from answers
    const getUnapprovedQuestionRubrics = (questionRubrics, answers, questionRubricIdToSkip) => {
        if (!questionRubrics || !answers) return []
        return questionRubrics.filter(qr => {
            if (qr.id === questionRubricIdToSkip) return false
            const answer = answers.find(a => a.questionRubricId === qr.id)
            return !answer?.approveStatus && (isNil(answer?.assigneeId) || answer.assigneeId === userId || isOwner)
        })
    }

    const navigateToNextUnreviewedAnswer = ({ lastReviewedQuestionRubricId }) => {
        if (gradingMode === GradingModes.MANUAL) return

        // Find the question containing the reviewed rubric
        const questionOfReviewedRubric = questions?.find(q =>
            q.questionRubrics?.some(qr => qr.id === lastReviewedQuestionRubricId),
        )

        // Find the question containing the current URL rubric param
        const questionOfUrlRubric = questions?.find(q => q.questionRubrics?.some(qr => qr.id === questionRubricId))

        // In BY_QUESTION mode, only navigate if reviewed rubric is in the same question as URL param
        const isInSameQuestion = questionOfReviewedRubric?.id === questionOfUrlRubric?.id
        if (questionRubricIdParam !== 'all' && !isInSameQuestion) return

        // Get unapproved rubrics across all questions (for BY_STUDENT mode)
        const unapprovedQuestionRubrics = getUnapprovedQuestionRubrics(
            allQuestionRubrics,
            submission?.answers,
            lastReviewedQuestionRubricId,
        )

        // Get unapproved rubrics within the current question only (for BY_QUESTION mode)
        const unapprovedRubricsInCurrentQuestion = getUnapprovedQuestionRubrics(
            questionOfReviewedRubric?.questionRubrics || [],
            submission?.answers,
            lastReviewedQuestionRubricId,
        )

        const gradingModeHandlers = {
            [GradingModes.BY_STUDENT]: () => {
                unapprovedQuestionRubrics.length === 0
                    ? navigateToNextSubmission()
                    : dispatch(textSubmissionDrawerActions.setFocusedQuestionRubricId(unapprovedQuestionRubrics[0].id))
            },
            [GradingModes.BY_RUBRIC]: () => {
                // In BY_RUBRIC mode, always advance to next submission after reviewing
                navigateToNextSubmission()
            },
            [GradingModes.BY_QUESTION]: () => {
                // In BY_QUESTION mode, find next unapproved rubric inside the same question or advance to next submission
                unapprovedRubricsInCurrentQuestion.length > 0
                    ? dispatch(
                          textSubmissionDrawerActions.setFocusedQuestionRubricId(
                              unapprovedRubricsInCurrentQuestion[0].id,
                          ),
                      )
                    : navigateToNextSubmission()
            },
        }

        if (questionRubricIdParam === 'all') gradingModeHandlers[GradingModes.BY_STUDENT]()
        else gradingModeHandlers[GradingModes.BY_QUESTION]()
    }

    const previousSubmissionIds = getGlobalPreviousSubmissionIds(groups, openedSubmissionId, 1)
    const nextSubmissionIds = getGlobalNextSubmissionIds(groups, openedSubmissionId, 1)

    const previousSubmissionExists = previousSubmissionIds.length > 0
    const nextSubmissionExists = nextSubmissionIds.length > 0

    return {
        navigateToPreviousSubmission,
        navigateToNextSubmission,
        previousSubmissionExists,
        nextSubmissionExists,
        previousSubmission: previousSubmissionIds[0],
        nextSubmission: nextSubmissionIds[0],
        navigateToNextUnreviewedAnswer,
    }
}

export const useQuestionFocusNavigationShortcuts = () => {
    const focusedQuestionRubricId = useSelector(textSubmissionDrawerSelectors.focusedQuestionRubricId)
    const dispatch = useDispatch()
    const { token } = useParams()
    const versionId = useVersionId()
    const { data: questions } = useFetchQuestionsQuery({ token, versionId })

    useEffect(() => {
        const handleKeyDown = e => {
            if (e.altKey && e.key === 'ArrowDown') {
                // Find the question that contains the currently focused questionRubric
                const currentQuestionIndex = questions?.findIndex(question =>
                    question.questionRubrics?.some(qr => qr.id === focusedQuestionRubricId),
                )
                if (currentQuestionIndex !== -1 && questions[currentQuestionIndex + 1]) {
                    // Focus the first questionRubric of the next question
                    const nextQuestionRubricId = questions[currentQuestionIndex + 1].questionRubrics?.[0]?.id
                    if (nextQuestionRubricId) {
                        dispatch(textSubmissionDrawerActions.setFocusedQuestionRubricId(nextQuestionRubricId))
                    }
                }
            }
            if (e.altKey && e.key === 'ArrowUp') {
                // Find the question that contains the currently focused questionRubric
                const currentQuestionIndex = questions?.findIndex(question =>
                    question.questionRubrics?.some(qr => qr.id === focusedQuestionRubricId),
                )
                if (currentQuestionIndex > 0 && questions[currentQuestionIndex - 1]) {
                    // Focus the first questionRubric of the previous question
                    const prevQuestionRubricId = questions[currentQuestionIndex - 1].questionRubrics?.[0]?.id
                    if (prevQuestionRubricId) {
                        dispatch(textSubmissionDrawerActions.setFocusedQuestionRubricId(prevQuestionRubricId))
                    }
                }
            }
        }
        window.addEventListener('keydown', handleKeyDown)
        return () => window.removeEventListener('keydown', handleKeyDown)
    }, [focusedQuestionRubricId, questions, dispatch])
}

export const useScrollToFocusedQuestionRubric = rescrollTrigger => {
    const focusedQuestionRubricId = useSelector(textSubmissionDrawerSelectors.focusedQuestionRubricId)
    const expandedQuestionIds = useSelector(textSubmissionDrawerSelectors.expandedQuestionIds)
    const dispatch = useDispatch()
    const { token } = useParams()
    const versionId = useVersionId()
    const { data: questions } = useFetchQuestionsQuery({ token, versionId })
    const prevFocusedIdRef = useRef(focusedQuestionRubricId)
    const prevRescrollTriggerRef = useRef(rescrollTrigger)

    // Find the parent questionId of the focused questionRubric
    const parentQuestionId = questions?.find(q => q.questionRubrics?.some(qr => qr.id === focusedQuestionRubricId))?.id

    const isParentExpanded = parentQuestionId ? expandedQuestionIds.includes(parentQuestionId) : false

    useEffect(() => {
        if (focusedQuestionRubricId && parentQuestionId) {
            // Only expand if focusedQuestionRubricId or rescrollTrigger actually changed
            // This prevents re-expanding after user manually collapses all questions
            const focusedIdChanged = prevFocusedIdRef.current !== focusedQuestionRubricId
            const rescrollTriggered = prevRescrollTriggerRef.current !== rescrollTrigger

            if (focusedIdChanged || rescrollTriggered) {
                dispatch(textSubmissionDrawerActions.expandQuestion(parentQuestionId))
            }

            prevFocusedIdRef.current = focusedQuestionRubricId
            prevRescrollTriggerRef.current = rescrollTrigger

            const timeoutId = setTimeout(
                () => {
                    const questionElement = document.querySelector(
                        `.grading-card[data-id="${focusedQuestionRubricId}"]`,
                    )
                    if (questionElement) {
                        questionElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
                    }
                },
                isParentExpanded ? 200 : 400,
            )

            return () => clearTimeout(timeoutId)
        }
    }, [focusedQuestionRubricId, parentQuestionId, rescrollTrigger, dispatch, isParentExpanded])
}

export const useAutofocusOpenedQuestionRubric = () => {
    const focusedQuestionRubricId = useSelector(textSubmissionDrawerSelectors.focusedQuestionRubricId)
    const openedSubmissionId = useSelector(submissionSelectors.openedSubmissionId)
    const dispatch = useDispatch()

    const { layerId: questionRubricIdParam } = useParams()
    const questionRubricId = Number(questionRubricIdParam)

    // Focus the questionRubric if it's not already focused and valid
    useEffect(() => {
        if (isNil(focusedQuestionRubricId) && isNumber(questionRubricId) && !Number.isNaN(questionRubricId)) {
            dispatch(textSubmissionDrawerActions.setFocusedQuestionRubricId(questionRubricId))
        }
    }, [questionRubricId, dispatch])

    // Update the focused questionRubric whenever openedSubmissionId changes
    useEffect(() => {
        if (isNumber(questionRubricId) && !Number.isNaN(questionRubricId)) {
            dispatch(textSubmissionDrawerActions.setFocusedQuestionRubricId(questionRubricId))
        }
    }, [openedSubmissionId, questionRubricId, dispatch])
}

const considerAsEscalated = escalated => {
    return [ExplainabilityStatuses.MANUAL_UNRESOLVED, ExplainabilityStatuses.UNEXPLAINABLE_UNRESOLVED].includes(
        escalated,
    )
}

export const useSelectNextEscalatedQuestionRubric = (answerId, submissionId) => {
    const onlyEscalatedParam = useQueryParam(FLAGGED_PARAM) === 'true'
    const { data: submission } = useFetchTextSubmissionDetailsQuery({
        submissionId,
    })

    const dispatch = useDispatch()

    return () => {
        if (!onlyEscalatedParam) {
            return
        }

        for (const answer of submission.answers) {
            if (considerAsEscalated(answer.escalated) && answer.id != answerId) {
                dispatch(textSubmissionDrawerActions.setFocusedQuestionRubricId(answer.questionRubricId))

                break
            }
        }
    }
}

export const useScrollToFirstEscalatedOnMount = submissionId => {
    const onlyEscalatedParam = useQueryParam(FLAGGED_PARAM) === 'true'
    const { data: submission } = useFetchTextSubmissionDetailsQuery({
        submissionId,
    })
    const focusedQuestionRubricId = useSelector(textSubmissionDrawerSelectors.focusedQuestionRubricId)

    const dispatch = useDispatch()

    useEffect(() => {
        if (!onlyEscalatedParam || !submission?.id || (focusedQuestionRubricId && !isNaN(focusedQuestionRubricId))) {
            return
        }

        for (const answer of submission.answers) {
            if (considerAsEscalated(answer.escalated)) {
                dispatch(textSubmissionDrawerActions.setFocusedQuestionRubricId(answer.questionRubricId))

                break
            }
        }
    }, [submission?.id, onlyEscalatedParam, dispatch, focusedQuestionRubricId, submission?.answers])
}

export const useAutoOpenSubmissionFromParams = groups => {
    const setQueryParams = useQueryParamSetter()
    const [openSubmissionId, focusQuestionId, resumeFromExplore] = useQueryParams([
        OPEN_SUBMISSION_ID_PARAM,
        FOCUS_QUESTION_ID_PARAM,
        RESUME_FROM_BUTTON_PARAM,
    ])
    const dispatch = useDispatch()
    const { token } = useParams()
    const versionId = useVersionId()

    useEffect(() => {
        if (!groups?.length) {
            return
        }

        if (openSubmissionId) {
            const payload = { id: +openSubmissionId, groups }
            if (resumeFromExplore === 'true') {
                payload.token = token
                payload.versionId = versionId
            }
            dispatch(submissionActions.openSubmission(payload))
        }

        if (focusQuestionId) {
            dispatch(textSubmissionDrawerActions.setFocusedQuestionRubricId(+focusQuestionId))
        }

        if (openSubmissionId || focusQuestionId || resumeFromExplore) {
            setQueryParams({
                [OPEN_SUBMISSION_ID_PARAM]: null,
                [FOCUS_QUESTION_ID_PARAM]: null,
                [RESUME_FROM_BUTTON_PARAM]: null,
            })
        }
    }, [openSubmissionId, focusQuestionId, resumeFromExplore, groups, token, versionId, dispatch, setQueryParams])
}

export const useAutoViewMode = submissionId => {
    const dispatch = useDispatch()
    const viewMode = useSelector(textSubmissionDrawerSelectors.viewMode)
    const { data: submission } = useFetchTextSubmissionDetailsQuery({ submissionId })

    const [isParsedModeDisabled, setIsParsedModeDisabled] = useState(false)

    useEffect(() => {
        if (submission?.answers?.length) {
            const allAnswersEmpty = submission.answers.every(answer => !answer.content || answer.content.trim() === '')

            setIsParsedModeDisabled(allAnswersEmpty)

            if (allAnswersEmpty && viewMode === ViewModes.PARSED) {
                dispatch(textSubmissionDrawerActions.setViewMode(ViewModes.PDF))
            }
        } else {
            setIsParsedModeDisabled(false)
        }
    }, [submission, dispatch, viewMode])

    return isParsedModeDisabled
}

export const useCloseAndNavigateToDashboard = () => {
    const flaggedParamRaw = useQueryParam(FLAGGED_PARAM)
    const onlyEscalatedParam = flaggedParamRaw === 'true'
    const { token } = useParams()
    const navigate = useNavigate()
    const location = useLocation()
    const dispatch = useDispatch()

    const closeAndNavigate = async () => {
        if (!onlyEscalatedParam) {
            return
        }

        const searchParams = new URLSearchParams(location.search)
        searchParams.delete(FOCUS_QUESTION_ID_PARAM)
        searchParams.delete(OPEN_SUBMISSION_ID_PARAM)
        searchParams.delete(FLAGGED_PARAM)

        const dashboardUrl = `${Routes.ASSIGNMENTS}/${token}/dashboard/${AssignmentSections.HOME}`
        const queryString = searchParams.toString()
        const fullUrl = queryString ? `${dashboardUrl}?${queryString}` : dashboardUrl
        navigate(fullUrl)
        setTimeout(() => {
            // A workaround to prevent the drawer from unmounting before navigation completes, leading to navigation not happening
            dispatch(submissionActions.closeSubmission())
        }, 1000)
    }

    return closeAndNavigate
}

export const useResetRefinementStartedOnQuestionRubricChange = () => {
    const { layerId: questionRubricId } = useParams()
    const dispatch = useDispatch()

    useEffect(() => {
        if (questionRubricId !== 'dashboard') {
            dispatch(unstartRefinementProcess())
        }
    }, [questionRubricId, dispatch])
}
